/**
 * Which shell serves a given in-app path — and, when that is not the shell you
 * are standing in, where the link should actually point.
 *
 * THE BUG THIS EXISTS TO STOP. `shellFromHost` split the app into four hosts,
 * and `visibleForShell` stopped each nav from OFFERING another product's work.
 * Neither of those touches a `<Link to="/subjects/12">` written inside a
 * brainstorm room. React Router happily served it, so the room's "Read by
 * <subject>" link opened a Publish screen at `brainstorm.lumnian.com/subjects/12`
 * — a whole product rendered inside a shell that is not supposed to have it,
 * with the wrong nav around it and a URL nobody could share. The split leaked at
 * every seam where one product deliberately points at another, which is exactly
 * where the products are most worth connecting.
 *
 * KEYED ON THE PATH, NOT ON THE CALL SITE. Each site could have been taught to
 * build its own absolute URL, and each new one would have to be taught again —
 * including `Artifacts`, whose links come from the server as `edit_path` and
 * could be any of the three products. One table, consulted by one component, is
 * what makes the answer automatic instead of remembered.
 *
 * Dependency-free so CRA's jest can test it, same as `shell.ts` itself.
 */
import { Shell, SHELLS, shellFromHost } from './shell';

/**
 * Path prefix → the shell that serves it. Longest match wins, so a future
 * `/deliver/x` living elsewhere would not be shadowed by `/deliver`.
 *
 * A path absent from this table is SHARED — Home, the artifacts shelf, settings,
 * account, login. Those render in every shell and must stay router-local.
 */
const PATH_SHELLS: [string, Shell][] = [
  ['/subjects', 'publish'],
  ['/projects', 'publish'], // redirects to /subjects, still a Publish URL
  ['/trending', 'publish'],
  ['/pages', 'publish'],
  ['/page-builder', 'publish'],
  ['/deliver', 'publish'],
  ['/link-builder', 'publish'],
  ['/boardroom', 'boardroom'],
  // Watch is sold WITH the boardroom rather than as its own product, so it is served from the
  // same shell. A subject outlives the room that created it, which is why it has its own path
  // rather than living under /boardroom/:id.
  ['/watch', 'boardroom'],
  ['/news-beats', 'beats'],
  // BILLING IS THE HOUSE'S, and now it has to be. Stripe sends the customer
  // back to a URL the SERVER builds from FRONTEND_URL, so the page has one
  // canonical home; leaving this shared meant `beats.lumnian.com/billing`
  // rendered perfectly well and a checkout started there returned somewhere
  // else. Two call sites already assumed the house (`billingHref`, the account
  // drawer) — this makes the assumption true.
  ['/billing', 'app'],
];

/** Strip query and hash; match on the path only. */
function pathOf(to: string): string {
  return to.split('?')[0].split('#')[0];
}

/**
 * The shell that owns this path, or null when it is shared.
 *
 * Matches on a path SEGMENT boundary, so `/pages` never claims `/pages-of-x`
 * and a hypothetical `/brainstorming` stays unclaimed.
 */
export function shellForPath(to: string): Shell | null {
  if (!to.startsWith('/')) return null; // absolute URL or a fragment: not ours
  const path = pathOf(to);
  let best: Shell | null = null;
  let bestLen = 0;
  for (const [prefix, shell] of PATH_SHELLS) {
    if (path !== prefix && !path.startsWith(`${prefix}/`)) continue;
    if (prefix.length > bestLen) {
      best = shell;
      bestLen = prefix.length;
    }
  }
  return best;
}

/**
 * The absolute URL for `to` on `shell`'s host, or null when a router link is
 * the right answer.
 *
 * Null covers three cases that all mean "do not leave the SPA": the path is
 * shared, it already belongs to this shell, or there is nowhere else to go —
 * a single-label host like `localhost` serves every shell from one origin, so
 * a full page load there would be a reload for no reason.
 */
export function crossShellHref(
  to: string,
  hostname: string,
  origin: string,
): string | null {
  const target = shellForPath(to);
  if (!target || target === shellFromHost(hostname)) return null;

  const parts = hostname.split('.');
  if (parts.length < 2) return null; // localhost / an IP: one origin serves all

  // If we cannot work out where we are, stay put. `new URL` throws on an empty
  // or malformed origin, and an exception thrown while deciding whether to
  // follow a link would take the screen down over a cosmetic routing question.
  try {
    const url = new URL(origin);
    url.hostname = `${SHELLS[target].host}.${parts.slice(1).join('.')}`;
    return `${url.origin}${to}`;
  } catch {
    return null;
  }
}
